<template>
    <div class="user-import-assignments">
        <div class="form">
            <section class="form-section">
                <div class="form-section-heading">
                    {{ trans('user-import.label.assign_usergroup') }}
                </div>

                <extendable-card-list
                    v-if="userGroups.length"
                    v-model="assignedUserGroups"
                    v-slot="slot"
                    :errors="errors"
                    :cards="userGroups"
                    sentence-when-empty="user-import.label.no-usergroup-selected"
                    data-can-be-added
                    data-can-be-deleted
                    data-can-be-edited
                >
                    <div class="form-row">
                        <div class="form-row__col">
                            <label class="label label--required">{{
                                trans('user-import.label.select_usergroup')
                            }}</label>
                            <select
                                v-model="
                                    assignedUserGroups[slot.index]['user_group']
                                "
                                :class="[
                                    'form-select',
                                    {
                                        'form-select--error':
                                            errors[
                                                `user_groups.${slot.index}.user_group_id`
                                            ],
                                    },
                                ]"
                            >
                                <option
                                    v-for="userGroup in userGroups"
                                    :value="userGroup"
                                    :key="userGroup.id"
                                    >{{ userGroup.name }}</option
                                >
                            </select>
                            <span
                                class="invalid-feedback"
                                v-if="
                                    errors[
                                        `user_groups.${slot.index}.user_group_id`
                                    ]
                                "
                            >
                                <strong>{{
                                    errors[
                                        `user_groups.${slot.index}.user_group_id`
                                    ][0]
                                }}</strong>
                            </span>
                        </div>

                        <div
                            class="form-row__col"
                            v-if="
                                userGroupHasCourses(
                                    assignedUserGroups[slot.index]
                                )
                            "
                        >
                            <label class="label label--required">{{
                                trans('user-import.label.select_debit_client')
                            }}</label>
                            <select
                                v-model="
                                    assignedUserGroups[slot.index]['client']
                                "
                                :class="[
                                    'form-select',
                                    {
                                        'form-select--error':
                                            errors[
                                                `user_groups.${slot.index}.client_id`
                                            ],
                                    },
                                ]"
                            >
                                <option
                                    v-for="client in debitClients"
                                    :value="client"
                                    :key="`client-${client.id}`"
                                    >{{ client.prefixed_name }}
                                    {{
                                        getClientLicenseDisplay(client)
                                    }}</option
                                >
                            </select>
                            <span
                                class="invalid-feedback"
                                v-if="
                                    errors[
                                        `user_groups.${slot.index}.client_id`
                                    ]
                                "
                            >
                                <strong>{{
                                    errors[
                                        `user_groups.${slot.index}.client_id`
                                    ][0]
                                }}</strong>
                            </span>
                        </div>

                        <div class="form-row__col" v-else></div>
                    </div>

                    <div
                        class="form-row"
                        v-if="
                            userGroupHasCourses(assignedUserGroups[slot.index])
                        "
                    >
                        <div class="form-row__col">
                            <form-date
                                name="start_date"
                                :initial-date="new Date(Date.now())"
                                v-model="
                                    assignedUserGroups[slot.index]['start_date']
                                "
                                :min-date="minDate"
                            >
                                {{
                                    trans('user-import.label.select_start_date')
                                }}
                            </form-date>
                        </div>

                        <div class="form-row__col">
                            <form-date
                                name="end_date"
                                v-model="
                                    assignedUserGroups[slot.index]['end_date']
                                "
                                :min-date="
                                    assignedUserGroups[slot.index][
                                        'start_date'
                                    ] || minDate
                                "
                            >
                                {{ trans('user-import.label.select_end_date') }}
                            </form-date>
                        </div>
                    </div>
                </extendable-card-list>

                <alert type="info" v-else>{{
                    trans('user-import.alert.no_usergroups_to_assign')
                }}</alert>
            </section>
        </div>

        <div class="form">
            <section class="form-section">
                <div class="form-section-heading">
                    {{ trans('user-import.label.assign_course') }}
                </div>

                <extendable-card-list
                    v-if="courses.length"
                    v-model="assignedCourses"
                    v-slot="slot"
                    :errors="errors"
                    :cards="courses"
                    sentence-when-empty="user-import.label.no-course-selected"
                    data-can-be-added
                    data-can-be-deleted
                    data-can-be-edited
                >
                    <div class="form-row">
                        <div class="form-row__col">
                            <label class="label label--required">{{
                                trans('user-import.label.select_course')
                            }}</label>
                            <select
                                v-model="assignedCourses[slot.index]['course']"
                                :class="[
                                    'form-select',
                                    {
                                        'form-select--error':
                                            errors[
                                                `courses.${slot.index}.course_id`
                                            ],
                                    },
                                ]"
                            >
                                <option
                                    v-for="course in courses"
                                    :value="course"
                                    :key="
                                        'assigned-courses-course-key-' +
                                            course.id
                                    "
                                    >{{ course.title }}</option
                                >
                            </select>
                            <span
                                class="invalid-feedback"
                                v-if="errors[`courses.${slot.index}.course_id`]"
                            >
                                <strong>{{
                                    errors[`courses.${slot.index}.course_id`][0]
                                }}</strong>
                            </span>
                        </div>

                        <div class="form-row__col">
                            <label class="label label--required">{{
                                trans('user-import.label.select_debit_client')
                            }}</label>
                            <select
                                v-model="assignedCourses[slot.index]['client']"
                                :class="[
                                    'form-select',
                                    {
                                        'form-select--error':
                                            errors[
                                                `courses.${slot.index}.client_id`
                                            ],
                                    },
                                ]"
                            >
                                <option
                                    v-for="client in debitClients"
                                    :value="client"
                                    :key="
                                        'assigned-courses-client-key-' +
                                            client.id
                                    "
                                    :disabled="isClientDisabled(client)"
                                    >{{ client.prefixed_name }}
                                    {{
                                        getClientLicenseDisplay(client)
                                    }}</option
                                >
                            </select>
                            <span
                                class="invalid-feedback"
                                v-if="errors[`courses.${slot.index}.client_id`]"
                            >
                                <strong>{{
                                    errors[`courses.${slot.index}.client_id`][0]
                                }}</strong>
                            </span>
                        </div>
                    </div>

                    <div class="form-row">
                        <div class="form-row__col">
                            <form-date
                                name="start_date"
                                :initial-date="new Date(Date.now())"
                                v-model="
                                    assignedCourses[slot.index]['start_date']
                                "
                                :min-date="minDate"
                            >
                                {{
                                    trans('user-import.label.select_start_date')
                                }}
                            </form-date>
                        </div>

                        <div class="form-row__col">
                            <form-date
                                name="end_date"
                                v-model="
                                    assignedCourses[slot.index]['end_date']
                                "
                                :min-date="
                                    assignedCourses[slot.index]['start_date'] ||
                                        minDate
                                "
                            >
                                {{ trans('user-import.label.select_end_date') }}
                            </form-date>
                        </div>
                    </div>
                </extendable-card-list>

                <alert type="info" v-else>{{
                    trans('user-import.alert.no_courses_to_assign')
                }}</alert>
            </section>
        </div>
    </div>
</template>

<script>
import { EventBus } from '../eventbus';

export default {
    props: {
        client: {
            type: Object,
            default: () => {
                return {};
            },
        },
        errors: Object,
        usersToBeImported: Array,
    },

    data() {
        return {
            courses: [],
            userGroups: [],
            assignedCourses: [],
            assignedUserGroups: [],
            debitClients: [],
            minDate: new Date(Date.now()),
        };
    },
    methods: {
        clearAssignments() {
            this.assignedCourses = [];
            this.assignedUserGroups = [];
        },
        getClientLicenseDisplay(client) {
            if (client.license_plan == 'license_plan_alpha') {
                return `(${this.trans('licenses.label.amount')}: ${
                    client.license_amount
                })`;
            }
            return '';
        },
        // really simple check, this could be made dynamic by how many things the user already has selected
        isClientDisabled(client) {
            if (client.license_plan == 'license_plan_alpha') {
                return client.license_amount < this.usersToBeImported.length;
            }
            return false;
        },

        updateOptions() {
            if (!this.client.id) return;

            this.clearAssignments();

            axios
                .get(`/api/clients/${this.client.id}/courses/`)
                .then(({ data }) => (this.courses = data));

            axios
                .get(`/api/clients/${this.client.id}/usergroups/`)
                .then(({ data }) => (this.userGroups = data));

            axios
                .get(
                    this.route(
                        'tenantapi.clients.debit.clients',
                        this.client.id
                    )
                )
                .then(({ data }) => (this.debitClients = data));
        },

        userGroupHasCourses(assignedUserGroup) {
            if (!assignedUserGroup.user_group) {
                return false;
            }

            return !!assignedUserGroup.user_group.countCourses;
        },
    },
    computed: {
        assignedCoursesToPassToParent() {
            return this.assignedCourses.filter(
                (assignedCourse, filterIndex) => {
                    // Don't add if required fields are not present
                    if (!assignedCourse.course) {
                        return false;
                    }

                    return true;
                }
            );
        },
        assignedUserGroupsToPassToParent() {
            return this.assignedUserGroups.filter(
                (assignedUserGroup, filterIndex) => {
                    // Don't add if required fields are not present
                    if (!assignedUserGroup.user_group) {
                        return false;
                    }

                    return true;
                }
            );
        },
    },
    mounted() {
        this.updateOptions();
    },
    watch: {
        client() {
            this.updateOptions();
        },

        assignedCourses() {
            this.$emit(
                'assigned-courses-changed',
                this.assignedCoursesToPassToParent
            );
        },

        assignedUserGroups() {
            this.$emit(
                'assigned-usergroups-changed',
                this.assignedUserGroupsToPassToParent
            );
        },
    },
};
</script>
